import { DialogComponent } from '@theme/dialog';

const unconfirmedMessage = 'We could not confirm the result. Your request may have been sent. Please check before submitting again.';

class CampInquiry extends DialogComponent {
  #controller;
  #body;
  #nativeSubmit;
  #pending = false;
  #verificationRequired = false;
  #session = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const form = this.querySelector('form');
    this.#nativeSubmit = form.submit;
    form.submit = () => this.#submit(form);
    this.#body = form.elements.namedItem('contact[body]')?.value;
    this.refs.dialog.close();
    this.addEventListener('submit', (event) => {
      if (event.target !== form) return;
      if (this.#verificationRequired) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const email = form.elements.namedItem('contact[email]');
      const body = form.elements.namedItem('contact[body]');
      body.value = `${this.#body}\nCollector email: ${email.value.trim()}`;
      form.setAttribute('aria-busy', 'true');
      // Shopify's challenge lives outside the dialog and must remain interactive.
      if (this.refs.dialog.matches(':modal')) {
        this.setAttribute('data-submitting', '');
        this.refs.dialog.close();
        this.refs.dialog.show();
      }
    }, { capture: true, signal });
    this.addEventListener('submit', (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      if (!form.hasAttribute('data-hcaptcha-bound')) this.#submit(form);
    }, { signal });
    this.addEventListener('dialog:close', () => {
      this.#session++;
      this.#pending = false;
      const button = form.querySelector('[type="submit"]');
      if (button) button.disabled = this.#verificationRequired;
      this.removeAttribute('data-submitting');
      form.removeAttribute('aria-busy');
    }, { signal });
    const result = this.querySelector('[role="alert"][data-inquiry-result]');
    if (result && !result.hidden) {
      this.showDialog();
      requestAnimationFrame(() => result.focus());
    }
  }

  async #submit(form) {
    if (this.#pending || this.#verificationRequired || !this.refs.dialog.open) return;
    const session = this.#session;
    this.#pending = true;
    const button = form.querySelector('[type="submit"]');
    const email = form.elements.namedItem('contact[email]');
    const message = form.querySelector('[role="alert"][data-inquiry-result]');
    form.elements.namedItem('contact[body]').value = `${this.#body}\nCollector email: ${email.value.trim()}`;
    button.disabled = true;
    message.hidden = true;
    form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(form.action, { method: 'POST', body: new FormData(form), signal: this.#controller.signal });
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (session !== this.#session) return;
      const result = page.getElementById(form.id)?.querySelector('[data-inquiry-result]:not([hidden])');
      const responseURL = new URL(response.url || form.action, location.href);
      if (response.ok && responseURL.origin === location.origin && /\/challenge\/?$/.test(responseURL.pathname)) {
        this.#verificationRequired = true;
        const verification = form.querySelector('[data-inquiry-verification]');
        verification.href = responseURL.href;
        verification.hidden = false;
        message.textContent = 'Shopify requires an additional verification. Continue with this request instead of submitting it again.';
        message.hidden = false;
      } else if (response.ok && result?.getAttribute('role') === 'status' && result.textContent.trim()) {
        email.value = '';
        await this.closeDialog();
      } else {
        message.textContent = result?.getAttribute('role') === 'alert' && result.textContent.trim() || (response.ok ? unconfirmedMessage : message.dataset.error);
        message.hidden = false;
      }
    } catch (error) {
      if (session === this.#session && error.name !== 'AbortError') {
        message.textContent = unconfirmedMessage;
        message.hidden = false;
      }
    } finally {
      if (session === this.#session) {
        button.disabled = this.#verificationRequired;
        form.removeAttribute('aria-busy');
        this.#pending = false;
        if (!message.hidden && this.refs.dialog.open) {
          this.removeAttribute('data-submitting');
          if (!this.refs.dialog.matches(':modal')) {
            this.refs.dialog.close();
            this.refs.dialog.showModal();
          }
          message.focus({ preventScroll: true });
        }
      }
    }
  }

  disconnectedCallback() {
    this.#session++;
    this.#controller?.abort();
    const form = this.querySelector('form');
    if (form && this.#nativeSubmit) form.submit = this.#nativeSubmit;
    super.disconnectedCallback();
  }
}

if (!customElements.get('camp-inquiry')) customElements.define('camp-inquiry', CampInquiry);
